import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import {
  getInvoiceMailOrderHints,
  type InvoiceMailOrderHint,
} from "@/lib/invoiceMail";
import { listOrdersWithFallback, type OrderHistoryRow } from "@/lib/orders";
import { orderLinePlainText } from "@/lib/orderDraftUtils";
import {
  getOrder,
  getOrderNotes,
  type ProjectNote,
} from "@/lib/projectOverview";
import type { Order } from "@/lib/projects";
import { InvoiceForOrderDialog } from "@/pages/bestellungen/InvoiceForOrderDialog";
import {
  fmtOrderDate,
  splitBestellnummern,
} from "@/pages/bestellungen/orderHistoryDisplay";
import {
  DOCUMENT_EDITOR_NESTED_OVERLAY_Z,
} from "@/pages/dokumente/documentEditorLayers";
import { NotesSection } from "@/projectOverview/sections/NotesSection";
import { useRealtime } from "@/lib/useRealtime";

function filterOrdersForProject(orders: OrderHistoryRow[], auftragId: number): OrderHistoryRow[] {
  return orders
    .filter((order) => {
      const inProjects = (order.projects || []).some((p) => Number(p.auftrag_id) === auftragId);
      const inLines = (order.positionen || []).some((p) => Number(p.auftrag_id) === auftragId);
      return inProjects || inLines;
    })
    .map((order) => ({
      ...order,
      positionen: (order.positionen || []).filter((p) => Number(p.auftrag_id) === auftragId),
    }));
}

function CompactOrderCard({
  order,
  invoiceHint,
  onViewInvoice,
}: {
  order: OrderHistoryRow;
  invoiceHint?: InvoiceMailOrderHint;
  onViewInvoice?: () => void;
}) {
  const cancelled = Boolean(order.cancelled_at);
  const tkList = splitBestellnummern(order.bestellnummern);
  const items = Array.isArray(order.positionen) ? order.positionen : [];
  const hasInvoice = (invoiceHint?.mail_count ?? 0) > 0;
  const invoicePending = Boolean(invoiceHint?.has_pending);

  return (
    <article
      className={clsx(
        "rounded-lg border border-separator bg-card p-2.5",
        cancelled && "opacity-70",
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-1.5">
        <div className="min-w-0 space-y-0.5">
          <div className="flex flex-wrap items-center gap-1">
            {tkList.length ? (
              tkList.map((tk) => (
                <span
                  key={tk}
                  className="rounded bg-fill-tertiary px-1.5 py-0.5 font-mono text-[11px] font-semibold text-label"
                >
                  {tk}
                </span>
              ))
            ) : (
              <span className="text-xs font-semibold text-label">Ohne Bestellnr.</span>
            )}
            {cancelled ? (
              <span className="rounded-full bg-status-red/15 px-1.5 py-0.5 text-[10px] font-semibold text-status-red">
                Storniert
              </span>
            ) : null}
          </div>
          <p className="truncate text-xs font-medium text-label">{order.supplier_name || "—"}</p>
          <p className="text-[11px] text-label-secondary">
            {fmtOrderDate(order.created_at)} · {items.length} Pos.
          </p>
        </div>
        {hasInvoice && onViewInvoice ? (
          <button
            type="button"
            className={
              invoicePending
                ? "shrink-0 rounded-full border border-status-orange/40 bg-status-orange/15 px-2 py-0.5 text-[11px] font-semibold text-status-orange hover:bg-status-orange/25"
                : "shrink-0 rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent hover:bg-accent/20"
            }
            title={
              invoicePending
                ? "Rechnung im Postfach – noch nicht abgelegt"
                : "Verknüpfte Eingangsrechnung ansehen"
            }
            onClick={onViewInvoice}
          >
            Rechnung
            {invoiceHint && invoiceHint.mail_count > 1 ? ` (${invoiceHint.mail_count})` : ""}
          </button>
        ) : null}
      </div>

      {items.length ? (
        <ul className="mt-2 space-y-0.5 border-t border-separator pt-2">
          {items.slice(0, 4).map((line, idx) => (
            <li
              key={`${order.id}-${idx}`}
              className="flex gap-1.5 text-[11px] text-label-secondary"
            >
              <span className="shrink-0 tabular-nums font-medium text-label">
                {line.menge ?? "—"}
              </span>
              <span className="shrink-0">{line.einheit || ""}</span>
              <span className="min-w-0 truncate">
                {orderLinePlainText(line.text || "") || "—"}
              </span>
            </li>
          ))}
          {items.length > 4 ? (
            <li className="text-[11px] text-label-tertiary">+ {items.length - 4} weitere …</li>
          ) : null}
        </ul>
      ) : null}
    </article>
  );
}

export function DocumentInvoiceContextPanel({
  auftragId,
  auftragsnummer,
}: {
  auftragId: number;
  auftragsnummer?: string | null;
}) {
  const { user } = useCurrentUser();
  const isBuero = userIsBuero(user);

  const [project, setProject] = useState<Order | null>(null);
  const [notes, setNotes] = useState<ProjectNote[]>([]);
  const [orders, setOrders] = useState<OrderHistoryRow[]>([]);
  const [invoiceHints, setInvoiceHints] = useState<Record<number, InvoiceMailOrderHint>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoiceDialogOrder, setInvoiceDialogOrder] = useState<OrderHistoryRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const needle = (auftragsnummer || "").trim();
      const [order, orderNotes, ordersRes] = await Promise.all([
        getOrder(auftragId),
        getOrderNotes(auftragId),
        needle
          ? listOrdersWithFallback({ q: needle })
          : Promise.resolve({ rows: [] as OrderHistoryRow[] }),
      ]);
      const filtered = filterOrdersForProject(ordersRes.rows || [], auftragId);
      setProject(order);
      setNotes(Array.isArray(orderNotes) ? orderNotes : []);
      setOrders(filtered);

      const ids = filtered.map((o) => o.id).filter((id) => Number.isFinite(id));
      if (ids.length) {
        try {
          const hints = await getInvoiceMailOrderHints(ids);
          setInvoiceHints(hints);
        } catch {
          setInvoiceHints({});
        }
      } else {
        setInvoiceHints({});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Projektdaten konnten nicht geladen werden.");
      setProject(null);
      setNotes([]);
      setOrders([]);
      setInvoiceHints({});
    } finally {
      setLoading(false);
    }
  }, [auftragId, auftragsnummer]);

  useEffect(() => {
    void load();
  }, [load]);

  useRealtime({
    scopes: ["auftraege:list", "orders:list"],
    domains: ["auftraege", "projects", "orders"],
    events: ["auftraege_changed", "projects_changed", "orders_changed"],
    enabled: !!auftragId,
    onChange: () => {
      void load();
    },
  });

  const ordersWithInvoice = useMemo(
    () => orders.filter((o) => (invoiceHints[o.id]?.mail_count ?? 0) > 0).length,
    [orders, invoiceHints],
  );

  if (loading && !project) {
    return (
      <aside className="gd-invoice-context">
        <div className="flex items-center gap-2 py-6 text-sm text-label-secondary">
          <Spinner className="h-4 w-4" />
          Projektkontext wird geladen …
        </div>
      </aside>
    );
  }

  if (error && !project) {
    return (
      <aside className="gd-invoice-context">
        <Alert variant="error">{error}</Alert>
      </aside>
    );
  }

  if (!project) return null;

  return (
    <aside className="gd-invoice-context" aria-label="Projektkontext">
      <section className="gd-invoice-context__section">
        <header className="gd-invoice-context__header">
          <h3 className="gd-invoice-context__title">Bestellungen</h3>
          <span className="text-[11px] tabular-nums text-label-tertiary">
            {orders.length}
            {ordersWithInvoice ? ` · ${ordersWithInvoice} mit Rechnung` : ""}
          </span>
        </header>
        {!orders.length ? (
          <p className="py-2 text-xs text-label-secondary">Keine Bestellungen für dieses Projekt.</p>
        ) : (
          <ul className="gd-invoice-context__orders space-y-2">
            {orders.map((order) => (
              <li key={order.id}>
                <CompactOrderCard
                  order={order}
                  invoiceHint={invoiceHints[order.id]}
                  onViewInvoice={() => setInvoiceDialogOrder(order)}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="gd-invoice-context__notes min-h-0 flex-1">
        <NotesSection
          orderId={auftragId}
          project={project}
          notes={notes}
          user={user}
          isBuero={isBuero}
          onChanged={() => void load()}
        />
      </section>

      <InvoiceForOrderDialog
        open={invoiceDialogOrder != null}
        orderId={invoiceDialogOrder?.id ?? null}
        bestellnummern={invoiceDialogOrder?.bestellnummern ?? null}
        onClose={() => setInvoiceDialogOrder(null)}
        zIndexClass={DOCUMENT_EDITOR_NESTED_OVERLAY_Z}
      />
    </aside>
  );
}
